import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';

import { DatePicker } from '@/components/Form/DatePicker';
import { LoadingButton } from '@/components/Form/LoadingButton';
import { TextInput } from '@/components/Form/TextInput';
import { Icon } from '@/components/Icon';
import { Form } from '@/components/Layout';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs';

import { IdentifyCustomerFormSchema } from '../constants/schema';
import { IdentifyCustomerForm as IdentifyCustomerFormType } from '../types/form';

export function IdentifyCustomerForm() {
  const { t, translateIfExists } = useTranslate();
  const { dateFormat } = useAppSelector(selectConfigsState);
  const theme = useTheme();

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<IdentifyCustomerFormType>({
    resolver: yupResolver(IdentifyCustomerFormSchema),
    defaultValues: {
      birthday: '',
      accountNumber: '',
    },
    mode: 'all',
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (data: IdentifyCustomerFormType) => Promise.resolve(data),
  });

  const onSubmit = (data: IdentifyCustomerFormType) => {
    console.log(data);
    mutate(data);
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} width="100%">
      <Stack direction="column" gap={3} alignItems="flex-start" width="100%">
        <Stack
          width="100%"
          flexDirection="column"
          gap={0.5}
          alignItems="flex-start"
        >
          <Typography
            variant="captionC1Regular"
            color={theme.palette.neutral[1000]}
          >
            {t('mobileAdoption.identifyCustomer.form.birthday.label')}
          </Typography>
          <Controller
            control={control}
            name="birthday"
            render={({ fieldState: { error } }) => (
              <DatePicker
                name="birthday"
                format={dateFormat}
                control={control}
                error={!!error}
                helperText={translateIfExists(error?.message)}
                disabled={isPending}
                disableFuture
              />
            )}
          />
        </Stack>
        <Stack
          width="100%"
          flexDirection="column"
          gap={0.5}
          alignItems="flex-start"
        >
          <Typography
            variant="captionC1Regular"
            color={theme.palette.neutral[1000]}
          >
            {t('mobileAdoption.identifyCustomer.form.accountNumber.label')}
          </Typography>
          <Controller
            control={control}
            name="accountNumber"
            render={({ fieldState: { error } }) => (
              <TextInput
                name="accountNumber"
                control={control}
                error={!!error}
                helperText={translateIfExists(error?.message)}
                disabled={isPending}
                placeholder={t(
                  'mobileAdoption.identifyCustomer.form.accountNumber.placeholder',
                )}
              />
            )}
          />
          <Stack direction="row" gap={1}>
            <Icon name="info-outline" />
            <Typography
              variant="captionC1Strong"
              color={theme.palette.neutral[700]}
            >
              {t(
                'mobileAdoption.identifyCustomer.form.accountNumber.description',
              )}
            </Typography>
          </Stack>
        </Stack>
        <LoadingButton
          type="submit"
          disabled={isPending || !isValid}
          isLoading={isPending}
          fullWidth
        >
          {t('mobileAdoption.identifyCustomer.form.button.continue')}
        </LoadingButton>
      </Stack>
    </Form>
  );
}
